import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { hasValue, formatOrDash } from 'utils/formatters';
import PaymentDetailsDialog from './PaymentDetailsDialog';

const DEFAULT_COLORS = {
  bg: 'rgba(156, 163, 175, 0.1)',
  border: '#9CA3AF',
  text: '#6B7280',
};

const HEADER_CELL_SX = { fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' };

/**
 * Shared table component for OverdueAgingSection and ProvisionForecastSection.
 *
 * Both sections render the same table structure (Period, USD, BRL, Count, action)
 * with only different data, colors, titles, and filter logic.
 */
const PeriodTable = ({ title, emptyMessage, data, colorMap, buildFilterParams }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({ title: '', filterParams: '' });

  const handleRowClick = (period) => {
    setDialogConfig({
      title: `${title}: ${period.label}`,
      filterParams: buildFilterParams(period),
    });
    setDialogOpen(true);
  };

  if (!data || data.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          height: '100%',
          textAlign: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.9rem' }}>
            {title}
          </Typography>
        </Box>
        <TableContainer sx={{ flex: 1 }}>
          <Table size="small" sx={{ height: '100%' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={HEADER_CELL_SX}>Period</TableCell>
                <TableCell align="right" sx={HEADER_CELL_SX}>USD</TableCell>
                <TableCell align="right" sx={HEADER_CELL_SX}>BRL</TableCell>
                <TableCell align="center" sx={{ ...HEADER_CELL_SX, width: 70 }}>Count</TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((period) => {
                const colors = colorMap[period.period] || DEFAULT_COLORS;
                return (
                  <TableRow
                    key={period.period}
                    onClick={() => handleRowClick(period)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      '&:hover': { bgcolor: colors.bg },
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: colors.text,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                          {period.label}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={hasValue(period.usd) ? 600 : 400}
                        color={hasValue(period.usd) ? 'text.primary' : 'text.disabled'}
                        sx={{ fontSize: '0.8rem' }}
                      >
                        {formatOrDash(period.usd, 'USD')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={hasValue(period.brl) ? 600 : 400}
                        color={hasValue(period.brl) ? 'text.primary' : 'text.disabled'}
                        sx={{ fontSize: '0.8rem' }}
                      >
                        {formatOrDash(period.brl, 'BRL')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {period.count > 0 ? (
                        <Chip
                          label={period.count}
                          size="small"
                          sx={{
                            minWidth: 32,
                            height: 22,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            bgcolor: colors.bg,
                            color: colors.text,
                            border: 'none',
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8rem' }}>
                          0
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View details">
                        <IconButton size="small" sx={{ color: 'text.secondary' }}>
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <PaymentDetailsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={dialogConfig.title}
        filterParams={dialogConfig.filterParams}
      />
    </Box>
  );
};

export default PeriodTable;
